<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import {
    UiAsyncCombobox,
    UiText,
    generateInitials,
    type ComboboxItemData,
} from '@shopexperts/shared';
import { useExpertSearch } from '../composables/useExpertSearch';

interface ExpertSelectorProps {
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    maxSelections?: number;
}

const props = withDefaults(defineProps<ExpertSelectorProps>(), {
    label: 'Search Experts',
    placeholder: 'Search experts by name or email...',
    disabled: false,
    maxSelections: 3,
});

const emit = defineEmits<{
    (e: 'update:selectedIds', expertIds: number[]): void;
}>();

// Internal state
const searchQuery = ref('');
const selectedItems = ref<ComboboxItemData[]>([]);

const { data, isSearching, hasResults } = useExpertSearch(searchQuery);

// Transform expert search results to combobox items
const searchResults = computed<ComboboxItemData[]>(() => {
    return data.value?.map((expert) => ({
        value: expert.id.toString(),
        label: expert.name,
        image: expert.avatar || undefined,
        rounded: true,
        description: expert.email || '',
        initials: generateInitials(expert.name),
        showImage: true,
    })) || [];
});

const showNoMatchMessage = computed(() => {
    return searchQuery.value.length >= 2 &&
        !isSearching.value &&
        !hasResults.value &&
        selectedItems.value.length === 0;
});

// Watch selectedItems and emit the numeric IDs
watch(selectedItems, (newItems) => {
    const numericIds = (newItems || [])
        .map(item => parseInt(item.value, 10))
        .filter(id => !isNaN(id));
    emit('update:selectedIds', numericIds);
}, { deep: true });

// Reset function
function reset() {
    searchQuery.value = '';
    selectedItems.value = [];
}

defineExpose({
    reset,
});
</script>

<template>
    <div class="flex flex-col gap-2">
        <UiText v-if="props.label" as="label" variant="p" class="font-semibold">
            {{ props.label }}
        </UiText>
        <UiAsyncCombobox 
            v-model="searchQuery" 
            v-model:selected-items="selectedItems"
            :data="searchResults" 
            :is-fetching="isSearching" 
            :disabled="props.disabled"
            :multiple="true"
            mode="preserve"
            display-mode="combobox"
            hide-combobox-when-empty 
            :placeholder="props.placeholder" 
        />

        <UiText v-if="showNoMatchMessage" as="p" variant="p-small" class="text-gray-600">
            No experts found matching your search.
        </UiText>

        <UiText v-if="selectedItems.length > 0" as="p" variant="p-small" class="text-gray-600">
            {{ selectedItems.length }} expert(s) selected (max {{ props.maxSelections }})
        </UiText>
    </div>
</template>
